'use client';

import React from 'react';
import useLoginModal from '@/app/hooks/useLoginModal';
import useAddContactModal from '@/app/hooks/useAddContactModal';
import { cn } from '@/lib/utils'; // caso já use essa helper
import { MessageCircle } from 'lucide-react';

type Variant = 'solid' | 'outline' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface AddContactButtonProps {
  userId?: string | null;
  label?: string;
  ariaLabel?: string;
  variant?: Variant;
  size?: Size;
  iconLeft?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
}

const variantClasses: Record<Variant, string> = {
  solid:
    'bg-purple-300 text-white hover:bg-purple-700 active:bg-purple-800 shadow-sm',
  outline:
    'border border-purple-600 text-purple-700 hover:bg-purple-50 active:bg-purple-100',
  ghost:
    'text-purple-300 hover:bg-purple-50 active:bg-purple-100',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm rounded-lg',
  md: 'h-10 px-4 text-sm rounded-xl',
  lg: 'h-11 px-5 text-base rounded-2xl',
};

const AddContactButton: React.FC<AddContactButtonProps> = ({
  userId,
  label = 'Fale conosco',
  ariaLabel,
  variant = 'solid',
  size = 'md',
  iconLeft = <MessageCircle size={18} aria-hidden="true" />,
  className,
  disabled,
  isLoading,
}) => {
  const login = useLoginModal();
  const addContact = useAddContactModal();

  const handleClick = () => {
    if (disabled || isLoading) return;
    if (userId) addContact.open();
    else login.open();
  };

  const computedAria = label ? undefined : (ariaLabel ?? 'Fale conosco');

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={computedAria}
      disabled={disabled || isLoading}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-medium transition-colors select-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-r00 focus-visible:ring-offset-2',
        'disabled:opacity-50 disabled:pointer-events-none',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      title={label}
    >
      {isLoading ? (
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-[2px] border-current border-t-transparent" />
      ) : (
        iconLeft
      )}
      {label && <span>{label}</span>}
    </button>
  );
};

export default AddContactButton;
